import { BCExtremeGridEntities, IComboboxLoadingArgs } from '@bimser/components';
import { IDictionary, MLHelper, isNullOrUndefined } from '@bimser/core';
import DataGrid from '../..';
import { FormControlsHelpers } from '../../../../../..';
import { getDataSourceData, geti18nValue } from '../../../../../../helper';
import { getActionValue } from '../../../../../../sagas/ruleValidation/action';
import { getValueControl } from '../../../../helper';
import { getCommonDefaultProperties } from './getColumnProperties';
import { IDataGridComboBoxColumn, IControlDataGridColumn } from '../../entities';
import { ICellChangeSelectOptions } from '../../../../entities/EventArgs';
import { IDataSource, MultiLanguageText } from '../../../../../../entities/common';
import { IControlProperties } from '../../../../../../entities';
import GetIconByName from '@bimser/icons/lib/getIcon16ByName';
import Styles from '../../assets/DataGridComponentStyles.scss';
import { Liquid } from 'liquidjs';
import { DataGridEditType } from '../../entities/operations';
import { Mobile } from '@bimser/common';

const liquidEngine = new Liquid();
import _merge from 'lodash/merge';
import _get from 'lodash/get';

async function getSelectColumn(
	this: DataGrid,
	column: IDataGridComboBoxColumn,
	index: number
): Promise<any> {
	const control = column.editControl;
	const currentColumn = this.__columns.find(({ name }) => name == column.name);
	let items = [];
	let data = [];
	let _getOptionsPromise;
	let isPromise;

	if (
		(Mobile.MobileView.isAndroidAvaliable || Mobile.MobileView.isIosAvaliable) &&
		control.dataSource?.name &&
		control.dataSourceType === 'dynamic' &&
		(!this.__columnDataSourceList[`${this.name}${column.name}`] || !currentColumn)
	) {
		this.subscribeOnPropertyChanged(column, index);

		const result = await getDynamicSelectColumnProperties.call(this, column, index);
		items = result.items;
		data = result.data;
	} else if (control.dataSource?.name && control.dataSourceType === 'dynamic') {
		isPromise = true;
		_getOptionsPromise = (args, column, search, value, isLoad) =>
			getOptionsPromise.call(this, args, column, search, value, isLoad);
	} else {
		items =
			currentColumn?.editorOptions.options ||
			control.items?.map(({ value, text, selected, icon, key }) => {
				const item: Record<string, any> = {
					key: key ? key : value.toString(),
					value,
					selected,
					text:
						text && typeof text == 'object' && Object.keys(text).length
							? geti18nValue(text, this.__form.currentLanguage)?.trim()
							: text?.toString().trim()
				};

				if (icon) {
					item.icon = GetIconByName(icon)?.info || null;
					item.externalData = { iconClassName: Styles.ComboboxIcon };
				}

				return item;
			}) ||
			[];
	}

	return {
		...getSelectColumnDefaultProperties.call(this, column, index),
		editorEvents: getEditorEvents.call(this, column),

		editorOptions: {
			changeSelectOptions: (args: ICellChangeSelectOptions) =>
				changeSelectOptions.call(this, args),
			options: items,
			value: items.find(item => item.selected),
			keyTextValue: true,
			disabled: !column.editingEnabled,
			allowClear: true,
			dataSource: control.dataSource,
			getOptionsPromise: _getOptionsPromise,
			mobileSearchPromise: (value: string, args) => {
				const currentColumn = this.columns.find(c => c.name == column.name);
				const control: any = currentColumn.editControl;
				let fieldParams = getFieldParams.call(this, control);
				const customFieldsParams = getCustomFieldsParams.call(this, currentColumn, args);
				fieldParams = _merge(fieldParams, customFieldsParams);

				return getDynamicSelectColumnProperties.call(
					this,
					column,
					index,
					fieldParams,
					value
				);
			},
			isPromise
		},
		data
	};
}

function __getReloadFilters(dataSource: IDataSource, search: string, valueType?: string) {
	if (search) {
		const displayExpr = dataSource.displayExpr;
		const valueExpr = dataSource.valueExpr;

		if (displayExpr.length === 1 && displayExpr.at(0) === valueExpr) {
			switch (valueType) {
				case 'integer': {
					const value = Number.parseInt(search);

					if (!Number.isNaN(value)) {
						return [
							{
								Filters: [
									{
										Column: valueExpr,
										Not: false,
										Type: 1,
										Value: value,
										ItemType: 3
									}
								],
								Operator: 2
							}
						];
					}

					return null;
				}
				case 'decimal': {
					const value = Number.parseFloat(search);

					if (!Number.isNaN(value)) {
						return [
							{
								Filters: [
									{
										Column: valueExpr,
										Not: false,
										Type: 1,
										Value: value,
										ItemType: 10
									}
								],
								Operator: 2
							}
						];
					}

					return null;
				}
			}
		}

		return [
			{
				Filters: displayExpr.map(exp => ({
					Column: exp,
					Not: false,
					Type: 6,
					Value: search,
					ItemType: 1
				})),
				Operator: 2
			}
		];
	}

	return [];
}

function __getReloadFilterValue(dataSource: IDataSource, search: string, valueType?: string) {
	if (search && dataSource?.valueExpr) {
		const valueExpr = dataSource.valueExpr;

		switch (valueType) {
			case 'integer': {
				const value = Number.parseInt(search);

				if (!Number.isNaN(value)) {
					return [
						{
							Filters: [
								{
									Column: valueExpr,
									Not: false,
									Type: 1,
									Value: value,
									ItemType: 3
								}
							],
							Operator: 2
						}
					];
				}

				return null;
			}
			case 'decimal': {
				const value = Number.parseFloat(search);

				if (!Number.isNaN(value)) {
					return [
						{
							Filters: [
								{
									Column: valueExpr,
									Not: false,
									Type: 1,
									Value: value,
									ItemType: 10
								}
							],
							Operator: 2
						}
					];
				}

				return null;
			}
		}

		return [
			{
				Filters: [
					{
						Column: valueExpr,
						Not: false,
						Type: 1,
						Value: search,
						ItemType: 1
					}
				],
				Operator: 2
			}
		];
	}

	return [];
}

function getCustomFieldsParams(this: DataGrid, column: any, args: any) {
	const control = column.editControl;
	const propertyPaths: string[] = [];

	if (control.dataSource?.parameters?.length) {
		control.dataSource.parameters.forEach(param => {
			if (param?.valueModifier == 'fieldSelection') {
				const fieldArr = param.field.split('.');
				const propertyPath = fieldArr.join('.');
				propertyPaths.push(propertyPath);
			}
		});

		const getCellValue = (value: any, type: DataGridEditType) => {
			switch (type) {
				case 'comboBox': {
					return value?.value || value?.text || value;
				}
				case 'multiLanguageTextBox': {
					if (!isNullOrUndefined(value)) {
						return (
							value[MLHelper.currentLanguage.culture] ||
							value[MLHelper.defaultLanguage.culture]
						);
					}
					break;
				}
				default:
					return value;
			}
		};
		if (propertyPaths.length && propertyPaths.filter(path => path.includes('columns.'))) {
			const rowData = args.data;
			const fieldParams: IDictionary<any> = {};
			Object.keys(rowData).forEach(key => {
				const column = this.columns.find(c => c.name == key);
				switch (column?.editType) {
					case 'comboBox': {
						if (column.dbType?.type === 'object') {
							const cellPropertyPaths = propertyPaths.filter(path =>
								path.includes(`columns.${key}.data`)
							);

							cellPropertyPaths.forEach(cellPropertyPath => {
								if (cellPropertyPath) {
									const cellValue = getCellValue(
										rowData[`__${key}`],
										this.columns.find(c => c.name == key)?.editType
									);
									const matcherColumnName =
										cellPropertyPath.split('.')[
											cellPropertyPath.split('.').length - 1
										];
									fieldParams[cellPropertyPath] =
										cellValue?.[matcherColumnName] || rowData[key] || null;
								}
							});
						} else {
							const cellPropertyPaths = propertyPaths.filter(path =>
								path.includes(`columns.${key}.value`)
							);

							cellPropertyPaths.forEach(cellPropertyPath => {
								if (cellPropertyPath) {
									const cellValue = getCellValue(
										rowData[key],
										this.columns.find(c => c.name == key)?.editType
									);
									fieldParams[cellPropertyPath] = cellValue || null;
								}
							});
						}

						break;
					}
					case 'textBox':
					case 'timePicker':
					case 'datePicker':
					case 'dateTimePicker':
					case 'numberBox':
					case 'checkBox':
					case 'dateRangePicker': {
						const cellPropertyPaths = propertyPaths.filter(path =>
							path.includes(`columns.${key}.value`)
						);

						cellPropertyPaths.forEach(cellPropertyPath => {
							if (cellPropertyPath) {
								const cellValue = getCellValue(
									rowData[key],
									this.columns.find(c => c.name == key)?.editType
								);
								fieldParams[cellPropertyPath] = cellValue || null;
							}
						});
						break;
					}
					case 'multiLanguageTextBox': {
						const cellPropertyPaths = propertyPaths.filter(path =>
							path.includes(`columns.${key}.culture`)
						);

						cellPropertyPaths.forEach(cellPropertyPath => {
							if (cellPropertyPath) {
								const path = cellPropertyPath.split('.');
								const culture = path[path.length - 1];
								const cellValue = _get(rowData, `${key}.${culture}`);
								fieldParams[cellPropertyPath] = cellValue || null;
							}
						});
						break;
					}
					default:
						break;
				}
			});
			return fieldParams;
		} else {
			return {};
		}
	} else {
		return {};
	}
}

async function getOptionsPromise(
	this: DataGrid,
	args: any,
	column: any,
	search?: string,
	value?: any,
	isload?: boolean
) {
	return new Promise<IComboboxLoadingArgs>(async resolve => {
		const emptyResovle = () => {
			resolve({ allow: true, options: [] });
		};

		try {
			const currentColumn = this.columns.find(c => c.name == column.name);
			const control: any = currentColumn.editControl;
			let fieldParams = getFieldParams.call(this, control);
			const customFieldsParams = getCustomFieldsParams.call(this, currentColumn, args);
			fieldParams = _merge(fieldParams, customFieldsParams);
			let response;

			try {
				response = await getDataSourceData({
					dataSource: control.dataSource,
					projectName: this.__form.projectName,
					clientCacheId: this.__form.__windowId,
					fieldParams,
					loadOptions: {
						pagination: {
							skip: 0,
							take: 50
						},
						sorts: null,
						filters: isload
							? __getReloadFilterValue(control?.dataSource, value, control?.valueType)
							: __getReloadFilters(control?.dataSource, search, control?.valueType),
						valueExpr: control?.dataSource?.valueExpr,
						distinct: true,
						filterNulls: true
					},
					reload: true,
					projectVersion: this.__form.packageVersion
				});
			} catch (error) {
				console.error(error);
			}

			const data = response ? FormControlsHelpers.getResultData(response.data) : null;

			if (data?.length) {
				if (
					!isload &&
					args.data?.[`__${currentColumn.name}`] &&
					!data.find(
						d =>
							d[control?.dataSource?.valueExpr] ==
							args.data?.[`__${currentColumn.name}`]?.[control?.dataSource?.valueExpr]
					)
				) {
					data.push(args.data[`__${currentColumn.name}`]);
				}

				const items = await getItemsFromData.call(this, data, [], currentColumn);
				resolve({ allow: true, options: items });
			} else {
				emptyResovle();
			}
		} catch (error) {
			console.warn(error);
			emptyResovle();
		}
	});
}

export function getDynamicSelectColumnProperties(
	this: DataGrid,
	column: IDataGridComboBoxColumn,
	index: number,
	customFieldsParams = {},
	searchValue: string = ''
): Promise<{ items: any[]; data: any[] }> {
	return new Promise(async (resolve, reject) => {
		const control = column.editControl;
		let items: Array<any> =
			this.dataSource && this.dataSourceType == 'relation'
				? this.__columns?.find(i => i.name === column.name)?.editorOptions?.options || []
				: [];
		let data = [];

		let fieldParams = getFieldParams.call(this, control);
		fieldParams = _merge(fieldParams, customFieldsParams);

		if (this.__form.displayMode === 'preview') {
			resolve({
				items: [],
				data: []
			});
		}

		try {
			Mobile.MobileView.toggleLoading(true, true);

			const response = await getDataSourceData({
				dataSource: control.dataSource,
				projectName: this.__form.projectName,
				clientCacheId: this.__form.__windowId,
				fieldParams,
				reload: true,
				loadOptions: {
					pagination: {
						skip: 0,
						take: 100
					},
					sorts: null,
					filters: __getReloadFilters(
						control?.dataSource,
						searchValue,
						control?.valueType
					)
				},
				projectVersion: this.__form.packageVersion
			});
			data = response ? FormControlsHelpers.getResultData(response.data) : null;

			if (data?.length) {
				items = await getItemsFromData.call(this, data, items, column);
			}
			resolve({
				items,
				data
			});
		} catch (error) {
			items = [];
			console.error(error);

			if (error instanceof Error && error.message === 'Invalid parameters') {
				resolve({
					items,
					data
				});

				return;
			}

			reject(error);
		} finally {
			Mobile.MobileView.toggleLoading(false, true);
		}
	});
}

async function changeSelectOptions(this: DataGrid, args: any) {
	const column = this.columns.find(col => col.name == args.columnName);
	const control = column?.editControl as any;

	if (control.dataSourceType == 'fields' && control.dataSource?.field) {
		const data = _get(args.data, control.dataSource.field);
		if (data?.length) {
			return getItemsFromData.call(this, data, [], column);
		}
	} else {
		this.emit('event:OnCellChangeSelectOptions', args);
		return args.items;
	}
}

function getEditorEvents(this: DataGrid, column: IControlDataGridColumn<IControlProperties>) {
	const args = { name: column.name, events: {} };
	this.emit('event:OnCellEditorInitilizing', args);
	return args.events;
}

function getFieldParams(this: DataGrid, control: any): IDictionary<any> {
	return control.dataSource.parameters?.reduce((result, param) => {
		if (param.valueModifier === 'valueEntry') {
			result[param.name] = param.value;
		} else if (param.valueModifier === 'fieldSelection') {
			const field = param.field.split('.');
			const controlId = field[0];
			const propPath = field.slice(1, field.length).join('.');
			const value = getValueControl(
				this.__form,
				controlId,
				getActionValue(controlId, propPath, this.__form.items as any)
			);

			result[param.field] = value;
		}
		return result;
	}, {});
}

async function getItemsFromData(
	this: DataGrid,
	data: Array<any>,
	items: Array<any>,
	column: IDataGridComboBoxColumn
) {
	const control = column.editControl;
	let displayFormat = control.dataSource.displayFormat;

	if (displayFormat.includes('.__userCulture')) {
		displayFormat = displayFormat.replace(
			'.__userCulture',
			`['${this.__form.currentLanguage.culture}']`
		);
	}

	const getItem = (item: any) => {
		const textML = new MultiLanguageText();
		const value =
			column.dbType.type == 'string'
				? item[control.dataSource.valueExpr]?.toString()
				: item[control.dataSource.valueExpr];

		if (column.dbType?.type === 'object') {
			textML.setText(liquidEngine.parseAndRenderSync(displayFormat, item));
		} else {
			textML.setText(value?.toString?.());
		}

		return {
			value,
			text: textML.getText()?.trim?.(),
			key: item[control.dataSource.valueExpr]?.toString(),
			externalData: item
		};
	};

	return data.reduce(
		(result, item) =>
			items?.some(i => i.key == item[control.dataSource.valueExpr].toString())
				? result
				: [...result, getItem(item)],
		items || []
	);
}

function getSelectColumnDefaultProperties(
	this: DataGrid,
	column: IDataGridComboBoxColumn,
	index: number
): BCExtremeGridEntities.IExtremeGridColumn {
	return {
		...getCommonDefaultProperties.call(this, column, index),
		tag: {
			dataType: 'object',
			editType: 'comboBox',
			getValueType: 'keyTextValue'
		},
		iconMatchers: column.iconMatchers,
		iconSourceField: column.iconSourceField,
		editingEnabled: column.editingEnabled
	};
}

export { getSelectColumn };
